import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import iconClose from '../../../assets/images/icon-close.svg';
import { goToPage, routeNaming, AppLink } from '../../../routes';
import { SearchStore } from '../../../stores/SearchStore';
import { Spinner } from '../../../common/spinner';
import { SUGGESTION_TYPES } from '../../../models/Suggestion';
import { IntraSearchFilter } from '../intra-search-filter/IntraSearchFilter';
import { enforceParameters } from '../../../hocs/enforce-parameters';
import { ConfigParametersStore } from '../../../stores/ConfigParametersStore';
// import { IntraFilterHelper } from '../../../helpers/IntraFilterHelper';
import iconSearch from './icon-search.svg';
import styles from './NavbarSearch.css';

const IPropTypes = {
  className: PropTypes.string,
  compressed: PropTypes.bool,
  focus: PropTypes.bool,
  onCloseClick: PropTypes.func,
  onSearchClick: PropTypes.func,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
};

const defaultProps = {
  className: '',
  compressed: false,
  focus: false,
  onCloseClick: () => {},
  onSearchClick: () => {},
};

// Default intranet filter value
const DEFAULT_INTRA_FILTER = 3;

class NavbarSearch extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: '',
      focused: false,
      dropdownEnabled: true,
      // filter: IntraFilterHelper.currentFilter(),
      filter: DEFAULT_INTRA_FILTER,
    };
    this.container = null;

    this._onBlur = this._onBlur.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._updateText = this._updateText.bind(this);
    this._handleDocumentClick = this._handleDocumentClick.bind(this);

    this._closeSearchDropdown = this._closeSearchDropdown.bind(this);

    this._onFilterChange = this._onFilterChange.bind(this);
    this._buildQueryParams = this._buildQueryParams.bind(this);
  }

  componentDidMount() {
    document.addEventListener('click', this._handleDocumentClick, false);
    document.addEventListener('touchend', this._handleDocumentClick, false);
    this._closeSearchDropdown();
  }

  // This is done to close search dropdown when clicking a suggestion.
  //
  // The main idea is to update dropdownEnabled state
  // if there is any change in the url pathname or search params.
  //
  // Note: It is necessary to update a component state inside
  //        componentDidUpdate even though it is not a good practice.
  //        For now, it is the best approach found.
  componentDidUpdate(prevProps) {
    const locationPathnameChanged = this.props.location.pathname !== prevProps.location.pathname;
    const locationSearchChanged = this.props.location.search !== prevProps.location.search;
    if (locationPathnameChanged || locationSearchChanged) {
      this._closeSearchDropdown();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this._handleDocumentClick, false);
    document.removeEventListener('touchend', this._handleDocumentClick, false);
  }

  _closeSearchDropdown() {
    this.setState({
      inputValue: '',
      dropdownEnabled: false,
    });
  }

  _handleDocumentClick(event) {
    if (this.container && !this.container.contains(event.target)) {
      if (this.state.dropdownEnabled) {
        this.setState({ dropdownEnabled: false });
      }
    }
  }

  _updateText(event) {
    const { currentTarget: { value } } = event;
    this.setState({
      inputValue: value,
      dropdownEnabled: true,
    });

    if (ConfigParametersStore.showIntranet) {
      SearchStore.suggest(value, this.state.filter || DEFAULT_INTRA_FILTER);
    } else {
      SearchStore.suggest(value);
    }
  }

  _onSubmit(event) {
    event.preventDefault();
    this.props.onSearchClick();
    if (!this.state.inputValue) {
      return;
    }
    goToPage(routeNaming.SEARCH_RESULTS, {}, this._buildQueryParams(this.state.inputValue));
  }

  _onCloseClick() {
    this.setState({
      inputValue: '',
      dropdownEnabled: false,
    });
    this.props.onCloseClick();
  }

  _onFocus() {
    this.setState({
      focused: true,
    });
  }

  _onBlur() {
    this.setState({
      focused: false,
    });
  }

  _onFilterChange(filter) {
    this.setState({
      filter,
    });

    // IntraFilterHelper.updateIntraFilter(filter);
  }

  _buildQueryParams(query, suggestionType, suggestionId) {
    const queryParams = {
      q: query,
    };
    if (suggestionType) {
      queryParams.st = suggestionType;
    }
    if (suggestionId) {
      queryParams.si = suggestionId;
    }
    if (ConfigParametersStore.showIntranet) {
      queryParams.filter = this.state.filter || DEFAULT_INTRA_FILTER;
    }
    return queryParams;
  }

  _renderSuggestions() {
    if (!SearchStore.suggestions) {
      return null;
    }
    const categorySuggestions = SearchStore.categorySuggestions.map(suggestion => (
      <AppLink
        className={styles.suggestion}
        key={suggestion.text}
        routeName={routeNaming.SEARCH_RESULTS}
        queryParams={this._buildQueryParams(
          suggestion.text,
          suggestion.type,
          suggestion.id,
        )}
      >
        {suggestion.text}
      </AppLink>
    ));
    // Add category section header
    if (categorySuggestions.length) {
      categorySuggestions.unshift((
        <p key="header-categories" className={styles.suggestionHeader}>
          Categorías
        </p>
      ));
    }
    const productSuggestions = SearchStore.productSuggestions.map((suggestion) => {
      if (suggestion.type === SUGGESTION_TYPES.SPECIFIC_PRODUCT) {
        return (
          <AppLink
            className={styles.suggestion}
            key={suggestion.text}
            routeName={routeNaming.SEARCH_RESULTS}
            queryParams={this._buildQueryParams(suggestion.text)}
          >
            {suggestion.text}
          </AppLink>
        );
      }
      return (
        <AppLink
          className={styles.suggestion}
          key={suggestion.text}
          routeName={routeNaming.PRODUCT_DETAIL}
          pathParams={{ id: suggestion.id }}
        >
          {suggestion.text}
        </AppLink>
      );
    });
    // Add product suggestions header
    if (productSuggestions.length) {
      productSuggestions.unshift((
        <p key="header-products" className={styles.suggestionHeader}>
          Productos
        </p>
      ));
    }
    return [
      ...categorySuggestions,
      ...productSuggestions,
    ];
  }

  render() {
    const focusClass = this.state.focused ? styles.focused : '';
    const compressedClass = this.props.compressed ? styles.compressed : '';
    const { intraSearchFilters } = ConfigParametersStore.parameters;

    return (
      <form
        className={[styles.container, compressedClass, this.props.className].join(' ')}
        onSubmit={this._onSubmit}
        ref={(container) => { this.container = container; }}
      >
        <div className={[styles.genericContainer, focusClass, compressedClass].join(' ')}>
          <button
            className={styles.searchButton}
            type="submit"
          >
            <img
              alt=""
              className={styles.searchIcon}
              src={iconSearch}
            />
          </button>
          {
            !this.props.compressed &&
            <div className={styles.inputContainer}>
              <input
                onFocus={this._onFocus}
                onBlur={this._onBlur}
                className={styles.input}
                onChange={this._updateText}
                ref={input => (input && this.props.focus ? input.focus() : null)}
                value={this.state.inputValue}
              />
              <button
                className={styles.closeButton}
                onClick={this._onCloseClick}
              >
                <img
                  alt=""
                  className={styles.closeIcon}
                  src={iconClose}
                />
              </button>
              { SearchStore.isFetchingSuggestionsData &&
                <Spinner className={styles.spinner} imgClassName={styles.spinnerImage} />
              }
            </div>
          }
        </div>
        {
          !this.props.compressed &&
          ConfigParametersStore.showIntranet &&
          <IntraSearchFilter
            onFilterChange={this._onFilterChange}
            filterOptions={intraSearchFilters}
            selectedFilter={this.state.filter || DEFAULT_INTRA_FILTER}
            className={styles.intraFilter}
          />
        }
        {
          this.state.dropdownEnabled &&
          !!SearchStore.suggestions &&
          !!SearchStore.suggestions.length &&
          <div className={styles.suggestionDropdown}>
            {this._renderSuggestions()}
          </div>
        }
      </form>
    );
  }
}

NavbarSearch.propTypes = IPropTypes;
NavbarSearch.defaultProps = defaultProps;

const ObservedNavbarSearch = enforceParameters(withRouter(observer(NavbarSearch)));

export { ObservedNavbarSearch as NavbarSearch };



// WEBPACK FOOTER //
// ./src/common/home-navbar/navbar-search/NavbarSearch.jsx